import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { navbarConfig } from '../config/navbarConfig';

const MobileNavbar = ({ 
  isOpen, 
  closeMenu, 
  activeSection,
  activeSubsection,
  setActiveSection,
  setActiveSubsection,
  isNavigatingRef
}) => {
  const [expandedItems, setExpandedItems] = useState({});
  const navigate = useNavigate();

  // Handle auto-expansion based on activeSection and activeSubsection props
  useEffect(() => {
    // Only expand if menu is open and not navigating
    if (!isOpen || (isNavigatingRef && isNavigatingRef.current)) return;
    if (activeSection) {
      // For sections without subsections, just expand the section
      if (navbarConfig[activeSection]?.route || 
          (navbarConfig[activeSection]?.items && 
           navbarConfig[activeSection].items.every(item => !item.subItems))) {
        setExpandedItems(prev => ({
          ...prev,
          [activeSection]: true
        }));
      }
      // For sections with subsections, we need to find which item to expand
      else if (typeof activeSubsection === 'number') {
        const itemKey = `${activeSection}-${activeSubsection}`;
        setExpandedItems(prev => ({
          ...prev,
          [itemKey]: true
        }));
      }
    }
  }, [activeSection, activeSubsection, isOpen]);

  const handleItemToggle = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleItemClick = (item) => {
    if (item.route) {
      if (isNavigatingRef) isNavigatingRef.current = true;
      setExpandedItems({});
      if (typeof setActiveSection === 'function') setActiveSection(null);
      if (typeof setActiveSubsection === 'function') setActiveSubsection(null);
      closeMenu();
      navigate(item.route);
    } else if (item.externalLink) {
      if (isNavigatingRef) isNavigatingRef.current = true;
      setExpandedItems({});
      if (typeof setActiveSection === 'function') setActiveSection(null);
      if (typeof setActiveSubsection === 'function') setActiveSubsection(null);
      closeMenu();
      window.location.href = item.externalLink;
    }
  };

  // Create a flattened array of section-subsection combinations
  const flattenedItems = [];
  
  Object.entries(navbarConfig).forEach(([sectionKey, section]) => {
    // Special case for About Us section
    if (sectionKey === 'whyMaxiom') {
      flattenedItems.push({
        id: sectionKey,
        title: section.title,
        items: section.items ? 
          section.items.flatMap(subsection => 
            subsection.subItems ? subsection.subItems : [subsection]
          ) : [],
        isSpecialSection: true
      });
    }
    // Handle direct routes at the section level - including Pricing
    else if (section.route || sectionKey === 'pricing') {
      flattenedItems.push({
        id: sectionKey,
        title: section.title,
        route: section.route,
        icon: section.icon,
        isDirectLink: true
      });
    } 
    // For sections with no subsections but with items (like Invest)
    else if (section.items && section.items.every(item => !item.subItems)) {
      // Special case: Render Invest section as static label with clickable items
      if (sectionKey === 'InvestManagement') {
        flattenedItems.push({
          id: sectionKey,
          title: section.title,
          items: section.items,
          isInvestInline: true
        });
      } else {
        flattenedItems.push({
          id: sectionKey,
          title: section.title,
          items: section.items,
          isSimpleSection: true
        });
      }
    }
    // Handle sections with subsections
    else if (section.items && section.items.length > 0) {
      section.items.forEach((subsection, index) => {
        flattenedItems.push({
          id: `${sectionKey}-${index}`,
          sectionKey, // Store original sectionKey for navigation events
          subsectionIndex: index, // Store index for navigation events
          sectionTitle: section.title,
          subsectionTitle: subsection.title,
          subsectionItems: subsection.subItems,
          route: subsection.route,
          externalLink: subsection.externalLink,
          icon: subsection.icon
        });
      });
    }
  });

  return (
    <div 
      className={`fixed top-[60px] left-0 w-full bg-white z-40 transition-transform duration-300 ease-in-out flex flex-col ${
        isOpen ? 'transform-none' : 'translate-x-full'
      }`}
      style={{ height: 'calc(100dvh - 60px)', maxHeight: '100dvh' }}
      data-testid="mobile-nav"
      aria-hidden={!isOpen}
    >
      {/* Scrollable content area with bottom padding to ensure all content is visible */}
      <div className="flex-1 overflow-y-auto pb-44 md:pb-44 gap-2 flex flex-col">
        {/* Iterate through flattened items */}
        {flattenedItems.map((item) => {
          // Check if this item should be automatically expanded
          const isActive = activeSection === item.id || 
                         (activeSection === item.sectionKey && 
                          activeSubsection === item.subsectionIndex);
          
          // Apply active class if needed
          const activeClass = isActive ? 'bg-gray-50' : '';
          
          return (
            <div key={item.id} className="border-b border-gray-100">
              {/* Direct link items */}
              {item.isDirectLink ? (
                <div 
                  className={`flex items-center gap-2 px-5 py-4 text-base font-semibold text-gray-800 cursor-pointer ${activeClass}`}
                  onClick={() => handleItemClick(item)}
                >
                  {item.icon && (
                    <span className="text-[#1C52A0] w-5 h-5 flex items-center justify-center">{item.icon}</span>
                  )}
                  <span>{item.title}</span>
                </div>
              ) : item.isSpecialSection ? (
                // Special case for About Us section
                <>
                  <div 
                    className={`flex justify-between items-center px-5 py-4 text-base font-semibold text-gray-800 cursor-pointer ${activeClass}`}
                    onClick={() => {
                      handleItemToggle(item.id);
                      setActiveSection(item.id);
                      setActiveSubsection(null);
                    }}
                  >
                    <span>{item.title}</span>
                    <span className={`transition-transform duration-300 ${expandedItems[item.id] ? 'rotate-180' : ''}`}>
                      &#9662;
                    </span>
                  </div>
                  
                  {expandedItems[item.id] && (
                    <div>
                      {item.items.map((subItem, subIndex) => (
                        <div 
                          key={`${item.id}-item-${subIndex}`}
                          className="flex items-center gap-4 pl-10 pr-5 py-4 text-base text-gray-800 cursor-pointer hover:bg-gray-50"
                          onClick={() => handleItemClick(subItem)}
                        >
                          {subItem.icon && (
                            <span className="text-[#1C52A0] w-5 h-5 flex items-center justify-center">{subItem.icon}</span>
                          )}
                          <span>{subItem.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : item.isInvestInline ? (
                // Render Invest as static label with clickable inline items
                <div className="px-5 py-4 text-base font-semibold text-gray-800 flex flex-wrap items-center gap-1">
                  <span>Invest -</span>
                  {item.items.map((subItem, idx) => (
                    <React.Fragment key={subItem.title}>
                      <span
                        className="text-[#1C52A0] underline cursor-pointer mx-1 font-semibold"
                        onClick={() => handleItemClick(subItem)}
                      >
                        {subItem.title}
                      </span>
                      {idx < item.items.length - 1 && <span>|</span>}
                    </React.Fragment>
                  ))}
                </div>
              ) : item.isSimpleSection ? (
                // Simple sections without subsections (like Invest)
                <>
                  <div 
                    className={`flex justify-between items-center px-5 py-4 text-base font-semibold text-gray-800 cursor-pointer ${activeClass}`}
                    onClick={() => {
                      handleItemToggle(item.id);
                      setActiveSection(item.id);
                      setActiveSubsection(null);
                    }}
                  >
                    <span>{item.title}</span>
                    <span className={`transition-transform duration-300 ${expandedItems[item.id] ? 'rotate-180' : ''}`}>
                      &#9662;
                    </span>
                  </div>
                  
                  {expandedItems[item.id] && (
                    <div>
                      {item.items.map((subItem, subIndex) => (
                        <div 
                          key={`${item.id}-item-${subIndex}`}
                          className="flex items-center gap-4 pl-10 pr-5 py-4 text-base text-gray-800 cursor-pointer hover:bg-gray-50"
                          onClick={() => handleItemClick(subItem)}
                        >
                          {subItem.icon && (
                            <span className="text-[#1C52A0] w-5 h-5 flex items-center justify-center">{subItem.icon}</span>
                          )}
                          <span>{subItem.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Section-Subsection combo items
                <>
                  <div 
                    className={`flex justify-between items-center px-5 py-4 text-base font-semibold text-gray-800 cursor-pointer ${activeClass}`}
                    onClick={() => {
                      if (item.subsectionItems) {
                        handleItemToggle(item.id);
                      } else {
                        handleItemClick(item);
                      }
                      setActiveSection(item.sectionKey);
                      setActiveSubsection(item.subsectionIndex);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {item.icon && (
                        <span className="text-[#1C52A0] w-5 h-5 flex items-center justify-center">{item.icon}</span>
                      )}
                      <span className="text-[#113262]">{item.sectionTitle}</span>
                      <span className="text-gray-400 mx-1">-</span>
                      <span>{item.subsectionTitle}</span>
                    </div>
                    
                    {item.subsectionItems && (
                      <span className={`transition-transform duration-300 ${expandedItems[item.id] ? 'rotate-180' : ''}`}>
                        &#9662;
                      </span>
                    )}
                  </div>
                  
                  {/* Expanded subsection items */}
                  {expandedItems[item.id] && item.subsectionItems && (
                    <div>
                      {item.subsectionItems.map((subItem, subIndex) => (
                        <div 
                          key={`${item.id}-item-${subIndex}`}
                          className="flex items-center gap-4 pl-10 pr-5 py-4 text-base text-gray-800 cursor-pointer hover:bg-gray-50"
                          onClick={() => handleItemClick(subItem)}
                        >
                          {subItem.icon && (
                            <span className="text-[#1C52A0] w-5 h-5 flex items-center justify-center">{subItem.icon}</span>
                          )}
                          <span>{subItem.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Fixed CTA Buttons at the bottom of the viewport */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.05)] p-4 z-50">
        <div className="flex flex-col gap-3 max-w-md mx-auto">
          <a 
            href="/contact-us" 
            rel="noopener noreferrer"
            className="flex justify-center items-center gap-2 py-3 px-5 bg-[#113262] text-white font-semibold rounded-md text-center"
          >
            Get In Touch <span>↗</span>
          </a>
          <a 
            href="https://app.maxiomwealth.com/login" 
            rel="noopener noreferrer"
            className="py-3 px-5 bg-gray-100 text-gray-800 font-semibold rounded-md text-center"
          >
            Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default MobileNavbar;